import { z } from "zod";
import { messagePartRefSchema } from "./messaging.js";
import { staffRequestStatusCodeValues } from "./staff-requests.js";
// The Stream messages of a concierge request (CHT-753, contract section 7.6; docs/engineering/concierge-requests.md section 5). The server
// posts them as `system` messages into the customer's channel, with the `lf` envelope below. They are NOT part of
// `lafayetteMessageEnvelopeSchema` (Milestone C): a client that does not know them shows the generic system line and reads the text of
// the message, and the request itself through `GET /customer/concierge-requests/:id` (and `GET /customer/actions/:id`).
//
// Rules, like every message envelope:
// - `z.strictObject`: an unknown key fails the parse. A new shape is a new `v`.
// - Ids and closed codes only. The text of the message is the closed status text of the state (`requestCustomerStatusText`). No note, no
//   reason, no supplier, no staff id, no work status, no amount, no link.
// - `statusCode` is a display code of the state when the message was posted. It is never an authority: a client reads the live request
//   before it shows a status or lets the customer act (a stale card or notice gets the current answer from the route).
// - This package cannot import the domain. `apps/api` has a test that builds each envelope with the domain builders and parses it here.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
/** One `concierge-request` part reference. `label` is `null`: the card reads the live request, and the id is the only reference. */
export const requestStreamPartRefSchema = messagePartRefSchema.refine((part) => part.k === "concierge-request" &&
    part.v === 1 &&
    part.refs.length === 1 &&
    part.refs.every((ref) => ref.type === "CONCIERGE_REQUEST" && ref.label === null), "a request message holds one concierge-request reference with no label");
const refsRequest = (value) => value.parts[0]?.refs[0]?.id.toLowerCase() === value.requestId.toLowerCase();
/** The first message of a request in the chat: the card. Posted once per request, when it is first submitted. */
export const requestCardEnvelopeSchema = z
    .strictObject({
    v: z.literal(1),
    kind: z.literal("REQUEST_CARD"),
    noticeId: uuid,
    requestId: uuid,
    parts: z.array(requestStreamPartRefSchema).length(1),
})
    .refine(refsRequest, { path: ["parts"], message: "the part must name this request" });
/** A short status line after a meaningful change. The text of the message is the closed status text of the current state. */
export const requestNoticeEnvelopeSchema = z
    .strictObject({
    v: z.literal(1),
    kind: z.literal("REQUEST_NOTICE"),
    noticeId: uuid,
    requestId: uuid,
    statusCode: z.enum(staffRequestStatusCodeValues),
    parts: z.array(requestStreamPartRefSchema).length(1),
})
    .refine(refsRequest, { path: ["parts"], message: "the part must name this request" });
export const requestStreamEnvelopeSchema = z.union([
    requestCardEnvelopeSchema,
    requestNoticeEnvelopeSchema,
]);
